import * as React from 'react'

import { Box, SearchField, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import ShowStatus from 'modules/payroll_bundle/components/common/components/ShowStatus'
import StatsCard from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/PaymentsPage/StatsCard'
import DisplayAmount from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/DisplayAmount'
import { useSearchFilter } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/useSearchFilter'
import type Payment from 'modules/payroll_bundle/store/models/Payment'
import type PaymentItem from 'modules/payroll_bundle/store/models/PaymentItem'

interface Props {
  isOpen: boolean
  onClose: () => void
  payment: Payment
}

interface ListProp {
  paymentItem: PaymentItem
}
const CENT = 100

const PaymentListItem = ({ paymentItem }: ListProp) => {
  const employeeId = paymentItem.attributes.employee_id
  const employee = employees.get(employeeId)
  const employeeName =
    employee &&
    (() => {
      const u = users.get(employee.attributes.access_id)
      return u
        ? getFullDisplayName({
            firstName: u.attributes.first_name,
            lastName: u.attributes.last_name,
            canSharePreferredName: employee.policy.can('sharePreferredName'),
            preferredName: employee.attributes.preferred_name,
          })
        : undefined
    })()
  const employeeIban = employee?.attributes.bank_number
  const amount = paymentItem.attributes.amount_in_cents / CENT

  return (
    <Box
      padding="s16"
      flexDirection="row"
      justifyContent="spaceBetween"
      width="full"
      background="grey000White"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
    >
      <Box gap="s4" justifyContent="flexStart">
        <Text.Normal weight="semibold" size="200">
          {employeeName}
        </Text.Normal>
        <Text.Normal size="100">{employeeIban}</Text.Normal>
      </Box>
      <Text.Normal weight="semibold" size="200">
        <DisplayAmount amount={String(amount)} />
      </Text.Normal>
    </Box>
  )
}

const PaymentDetailSidepanel = ({ isOpen, onClose, payment }: Props) => {
  const createdAtText = moment(payment.attributes.created_at).format('MMM D h:mm')
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const itemsToShow = useSearchFilter(debouncedSearch, payment.paymentItems).get()

  return (
    <Sidepanel
      open={isOpen}
      title={i18n.t('payroll_bundle.results_payments.payments.list.title', { createdAtText })}
      onClose={onClose}
      overlay={false}
      closeLabel={i18n.t('common.close')}
      boxPadding={false}
    >
      <Box gap="s32" minHeight="full">
        <Box alignSelf="center" paddingTop="s32">
          <ShowStatus status={payment.attributes.status} />
        </Box>
        <Box alignItems="center">
          <StatsCard
            totalAmount={payment.paymentsTotalAmount}
            employeeCount={payment.totalEmployees}
            sizeSmaller
          />
        </Box>
        <Box padding="s16" background="grey200" justifyContent="flexStart" gap="s16" flexGrow>
          <SearchField variant="big" search={search} onSearch={onSearch} placeholder="" />
          <Box gap="s16">
            {itemsToShow.map((paymentItem) => {
              return <PaymentListItem key={paymentItem.id} paymentItem={paymentItem} />
            })}
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}

export default observer(PaymentDetailSidepanel)
